import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Loader2, Flame, Sun } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

function defaultWaUrl(phone, name) {
  const digits = String(phone || "").replace(/[^0-9]/g, "");
  const e164 = digits.length === 10 ? `91${digits}` : digits;
  const first = (name || "there").split(" ")[0];
  const msg = `Hi ${first}, this is from MedAdmits — following up on your scholarship enquiry. Is now a good time to chat?`;
  return `https://wa.me/${e164}?text=${encodeURIComponent(msg)}`;
}

/**
 * Click-to-WhatsApp button with a template picker + post-send "Replied?" prompt.
 * Props: lead, disabled, baseBtn (className), onAfterSend (optional), onLogAttempt
 */
export default function WhatsAppTemplatePicker({ lead, disabled, baseBtn, onLogAttempt, onChanged, onSent }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [replyFor, setReplyFor] = useState(false);
  const [savingTemp, setSavingTemp] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/wa-templates`);
      setTemplates(data.templates || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadTemplates();
  };

  const openWhatsapp = (url) => window.open(url, "_blank", "noopener");

  const sendTemplate = async (t) => {
    setOpen(false);
    try {
      await api.post(`/admin/leads/${lead.id}/wa-template-sent`, { template_id: t.id });
      onSent?.();
    } catch {
      /* still open whatsapp */
    }
    openWhatsapp(t.wa_url || defaultWaUrl(lead.phone, lead.name));
    setReplyFor(true);
  };

  const quickMessage = () => {
    setOpen(false);
    onLogAttempt?.("whatsapp");
    onSent?.();
    openWhatsapp(defaultWaUrl(lead.phone, lead.name));
    setReplyFor(true);
  };

  const markReply = async (temperature) => {
    setSavingTemp(true);
    try {
      await api.post(`/admin/leads/${lead.id}/wa-reply`, { temperature, replied: true });
      if (temperature) toast.success(`Marked ${temperature}`);
      onChanged?.();
    } catch {
      toast.error("Could not update");
    } finally {
      setSavingTemp(false);
      setReplyFor(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            aria-label={`WhatsApp ${lead.name}`}
            className={`${baseBtn} text-[#25D366] hover:bg-[#25D366]/10`}
            data-testid={`contact-whatsapp-${lead.id}`}
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0" onClick={(e) => e.stopPropagation()} data-testid="wa-template-picker">
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-[#0A2540]">
            Pick a WhatsApp template
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" /> Loading…</div>
            ) : templates.length === 0 ? (
              <div className="px-3 py-3 text-center text-[11px] text-slate-400">No templates for this stage. Add them in Settings → WhatsApp.</div>
            ) : (
              templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => sendTemplate(t)}
                  className="block w-full px-3 py-2 text-left transition-colors hover:bg-slate-50"
                  data-testid={`wa-pick-${t.id}`}
                >
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#0A2540]">
                    {t.name}
                    {t.media_type && <span className="rounded bg-blue-50 px-1 text-[9px] text-[#1877F2]">{t.media_type}</span>}
                  </div>
                  <div className="truncate text-[11px] text-slate-500">{t.rendered}</div>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={quickMessage}
            className="w-full border-t border-slate-100 px-3 py-2 text-left text-[11px] text-slate-500 hover:bg-slate-50"
            data-testid="wa-quick-message"
          >
            Quick message (no template)
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={replyFor} onOpenChange={setReplyFor}>
        <DialogContent className="max-w-sm" data-testid="wa-reply-prompt">
          <DialogHeader>
            <DialogTitle>Did {lead.name?.split(" ")[0] || "they"} reply?</DialogTitle>
            <DialogDescription>Mark the lead's temperature if they responded.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button onClick={() => markReply("Hot")} disabled={savingTemp} className="bg-rose-600 hover:bg-rose-700" data-testid="wa-reply-hot">
              <Flame className="mr-1.5 h-4 w-4" /> Replied — mark Hot
            </Button>
            <Button onClick={() => markReply("Warm")} disabled={savingTemp} className="bg-amber-500 hover:bg-amber-600" data-testid="wa-reply-warm">
              <Sun className="mr-1.5 h-4 w-4" /> Replied — mark Warm
            </Button>
            <Button variant="outline" onClick={() => setReplyFor(false)} data-testid="wa-reply-none">
              Not yet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
